import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { toast } from "sonner";
import { 
  Package, 
  RefreshCw, 
  ShoppingCart, 
  Clock, 
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Truck,
  XCircle
} from "lucide-react";
import { API } from "../apiConfig";


const OrderHistoryPage = () => {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, orderId: null });
  const [reorderResult, setReorderResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/reorder/history`, { headers }),
        axios.get(`${API}/reorder/suggestions`, { headers })
      ]);
      setOrders(ordersRes.data || []);
      setSuggestions(suggestionsRes.data?.suggestions || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (orderId) => {
    setReordering(orderId);
    try {
      const res = await axios.post(`${API}/reorder/order/${orderId}`, {}, { headers });
      setReorderResult(res.data);
      toast.success(`${res.data.items_added} items added to cart`);
      setConfirmDialog({ open: false, orderId: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reorder");
    } finally {
      setReordering(null);
    }
  };

  const handleReorderItem = async (productId, variantId, quantity) => {
    try {
      await axios.post(`${API}/reorder/item`, {
        product_id: productId,
        variant_id: variantId,
        quantity
      }, { headers });
      toast.success("Item added to cart");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add item");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-700", icon: Clock },
      processing: { color: "bg-blue-100 text-blue-700", icon: Package },
      shipped: { color: "bg-purple-100 text-purple-700", icon: Truck },
      delivered: { color: "bg-green-100 text-green-700", icon: CheckCircle2 },
      completed: { color: "bg-green-100 text-green-700", icon: CheckCircle2 },
      cancelled: { color: "bg-red-100 text-red-700", icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 text-center">
          <h1 className="text-2xl font-medium mb-4">Please sign in to view your orders</h1>
          <Link to="/login">
            <Button className="rounded-full">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="order-history-page">
      <Navigation />

      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold">Order History</h1>
              <p className="text-muted-foreground">
                {orders.length} {orders.length === 1 ? 'order' : 'orders'}
              </p>
            </div>
          </div>

          {/* Quick Reorder Suggestions */}
          {suggestions.length > 0 && (
            <Card className="mb-8 border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Quick Reorder
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Based on your order history
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {suggestions.slice(0, 6).map((product) => (
                    <div 
                      key={product.product_id}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border"
                    >
                      <img
                        src={product.images?.[0] || "https://via.placeholder.com/60"}
                        alt={product.title}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.title}</p>
                        <p className="text-xs text-muted-foreground">
                          Ordered {product.times_ordered}x
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => handleReorderItem(product.product_id, null, 1)}
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Orders List */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-1/4 mb-4" />
                    <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : orders.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-medium mb-2">No orders yet</h2>
                <p className="text-muted-foreground mb-6">
                  Start shopping to see your order history here
                </p>
                <Link to="/products">
                  <Button className="rounded-full">Browse Collection</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {orders.map((order) => (
                <AccordionItem 
                  key={order.order_id} 
                  value={order.order_id}
                  className="border rounded-xl overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-muted/50">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="text-left">
                        <p className="font-medium">Order #{order.order_id.slice(-8)}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(order.created_at)} • {order.items?.length || 0} items
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(order.status)}
                        <span className="font-medium">{formatPrice(order.total)}</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  
                  <AccordionContent className="px-6 pb-4">
                    {/* Order Items */}
                    <div className="space-y-3 mb-4">
                      {order.items?.map((item, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg"
                        >
                          <img
                            src={item.image || item.current_product?.images?.[0] || "https://via.placeholder.com/80"}
                            alt={item.title}
                            className="w-16 h-16 object-cover rounded"
                          />
                          <div className="flex-1">
                            <Link 
                              to={`/products/${item.product_id}`}
                              className="font-medium hover:text-primary"
                            >
                              {item.title}
                            </Link>
                            <p className="text-sm text-muted-foreground">
                              Qty: {item.quantity} × {formatPrice(item.price)}
                            </p>
                            {item.price_changed && (
                              <p className="text-xs text-amber-600 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Price changed to {formatPrice(item.current_price)}
                              </p>
                            )}
                            {!item.is_available && (
                              <p className="text-xs text-red-600">Currently unavailable</p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            disabled={!item.is_available}
                            onClick={() => handleReorderItem(item.product_id, item.variant_id, item.quantity)}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Reorder
                          </Button>
                        </div>
                      ))}
                    </div>

                    {/* Reorder All Button */}
                    <div className="flex justify-end pt-3 border-t">
                      <Button
                        onClick={() => setConfirmDialog({ open: true, orderId: order.order_id })}
                        disabled={reordering === order.order_id}
                        className="rounded-full"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${reordering === order.order_id ? 'animate-spin' : ''}`} />
                        {reordering === order.order_id ? 'Adding to Cart...' : 'Reorder All Items'}
                      </Button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </main>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, orderId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reorder All Items?</DialogTitle>
            <DialogDescription>
              This will add all available items from this order to your cart. 
              Items that are out of stock or unavailable will be skipped.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, orderId: null })}>
              Cancel
            </Button>
            <Button onClick={() => handleReorder(confirmDialog.orderId)}>
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={!!reorderResult} onOpenChange={() => setReorderResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Items Added to Cart</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-green-600 font-medium">
              ✓ {reorderResult?.items_added} items added to your cart
            </p>
            
            {reorderResult?.unavailable_items?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-amber-600 mb-2">
                  Some items were unavailable:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {reorderResult.unavailable_items.map((item, i) => (
                    <li key={i}>• {item.title}: {item.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {reorderResult?.price_changes?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Price changes:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {reorderResult.price_changes.map((item, i) => (
                    <li key={i}>
                      • {item.title}: {formatPrice(item.old_price)} → {formatPrice(item.new_price)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReorderResult(null)}>
              Continue Shopping
            </Button>
            <Link to="/cart">
              <Button>
                <ShoppingCart className="w-4 h-4 mr-2" />
                View Cart
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default OrderHistoryPage;
